import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, MessageSquare, Zap, Shield, Globe, BarChart, Users, Sparkles, PlayCircle } from 'lucide-react';
import { HeroSlider } from '../components/hero/HeroSlider';
import { LandingHeader } from '../components/layout/landing-header';
import { PRIVATE_BETA_ENABLED } from '../config/api';

const NewHomePage: React.FC = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (PRIVATE_BETA_ENABLED) {
      // In private beta, go to waitlist start page
      navigate('/start');
    } else {
      // Normal flow
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const onboardingUrl = isLocalhost 
        ? 'http://localhost:3000/signup' 
        : 'https://onboarding.pagemind.ai/signup';
      window.location.href = onboardingUrl;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section with Video Slider */}
      <div className="relative">
        {/* Header - Overlaid on video */}
        <LandingHeader showBackButton={false} currentPage="home" variant="transparent" />

        <HeroSlider />
      </div>

      {/* Original Hero Content - Now Below Video */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {PRIVATE_BETA_ENABLED && (
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full text-sm font-medium shadow-lg">
              <Sparkles className="h-4 w-4" />
              <span>Private Beta - Limited Access</span>
            </div>
          )}
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            How PageMind Works
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Our AI understands your website content and helps visitors find exactly what they need, instantly.
          </p>
          <div className="flex justify-center space-x-4">
            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className="flex items-center"
            >
              {PRIVATE_BETA_ENABLED ? 'Join Waitlist' : 'Start Free Trial'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            {!PRIVATE_BETA_ENABLED && (
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/pricing')}
              >
                View Pricing
              </Button>
            )}
          </div>
          <p className="mt-4 text-sm text-gray-500">
            {PRIVATE_BETA_ENABLED 
              ? 'Early access for selected users • Get notified when we launch'
              : 'No credit card required • 10 free conversations daily'
            }
          </p>
        </div>
      </section>

      {/* Video Placeholder Section - Only in Private Beta */}
      {PRIVATE_BETA_ENABLED && (
        <section className="bg-gradient-to-b from-gray-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                See PageMind in Action
              </h3>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Watch how leading companies use PageMind to transform their customer experience
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video max-w-4xl mx-auto">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/Fjf7xtvzfHQ?rel=0&showinfo=0&modestbranding=1"
                title="PageMind Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Everything You Need to Engage Visitors
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <MessageSquare className="h-12 w-12 text-blue-600 mb-4" />
            <h4 className="text-xl font-semibold mb-3">Intelligent Conversations</h4>
            <p className="text-gray-600">
              Our AI understands context and intent, providing accurate answers from your website content.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <Zap className="h-12 w-12 text-blue-600 mb-4" />
            <h4 className="text-xl font-semibold mb-3">Instant Setup</h4>
            <p className="text-gray-600">
              Just add a simple script to your website. PageMind automatically learns from your content.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <Shield className="h-12 w-12 text-blue-600 mb-4" />
            <h4 className="text-xl font-semibold mb-3">Privacy First</h4>
            <p className="text-gray-600">
              GDPR compliant. Your data stays yours. No training on your content without permission.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <Globe className="h-12 w-12 text-blue-600 mb-4" />
            <h4 className="text-xl font-semibold mb-3">Multi-Language</h4>
            <p className="text-gray-600">
              Automatically detects and responds in your visitor's language. 50+ languages supported.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <BarChart className="h-12 w-12 text-blue-600 mb-4" />
            <h4 className="text-xl font-semibold mb-3">Analytics & Insights</h4>
            <p className="text-gray-600">
              Understand what visitors are asking and identify content gaps to improve your site.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <Users className="h-12 w-12 text-blue-600 mb-4" />
            <h4 className="text-xl font-semibold mb-3">Lead Generation</h4>
            <p className="text-gray-600">
              Capture visitor information naturally through conversations and grow your customer base.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How PageMind Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h4 className="text-xl font-semibold mb-3">Add Our Script</h4>
              <p className="text-gray-600">
                Copy and paste a single line of code to your website. Works with any platform.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h4 className="text-xl font-semibold mb-3">AI Learns Your Content</h4>
              <p className="text-gray-600">
                PageMind automatically crawls and understands your pages, products, and services.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h4 className="text-xl font-semibold mb-3">Engage Visitors</h4>
              <p className="text-gray-600">
                Your AI assistant is ready to help visitors find information and take action.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h3 className="text-3xl font-bold text-gray-900 mb-6">
          Ready to Transform Your Website?
        </h3>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Join thousands of businesses using PageMind to provide instant, intelligent support to their visitors.
        </p>
        <div className="flex justify-center space-x-4">
          <Button 
            size="lg" 
            onClick={handleGetStarted}
            className="flex items-center"
          >
            {PRIVATE_BETA_ENABLED ? 'Join Waitlist' : 'Start Free Trial'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            onClick={() => navigate('/contact')}
          >
            Talk to Sales
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h5 className="font-semibold mb-4">Product</h5>
              <ul className="space-y-2">
                <li><a href="https://www.pagemind.ai/features" className="text-gray-400 hover:text-white">Features</a></li>
                <li><a href="https://www.pagemind.ai/docs" className="text-gray-400 hover:text-white">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Company</h5>
              <ul className="space-y-2">
                <li><a href="https://www.pagemind.ai/about" className="text-gray-400 hover:text-white">About</a></li>
                <li><a href="https://www.pagemind.ai/contact" className="text-gray-400 hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Legal</h5>
              <ul className="space-y-2">
                <li><a href="https://www.pagemind.ai/privacy" className="text-gray-400 hover:text-white">Privacy Policy</a></li>
                <li><a href="https://www.pagemind.ai/terms" className="text-gray-400 hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Connect</h5>
              <p className="text-gray-400">
                Stay updated with PageMind news and updates.
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>&copy; 2024 PageMind. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewHomePage;